import { isNil } from 'lodash-es';
import { useEffect, useState } from 'react';
import createNetworkingClient from 'utils/networking-client';
import { injectGoogleScript } from 'externalSocialPartners';
import { timeout } from 'utils/dev-utils';
import { AuthorizationStatus } from './constants/auth-status-constants';

const PEOPLE_BASE_URL = 'https://people.googleapis.com/v1/';

/**
 * Fetches a list of contacts from an authenticated google account.
 */
const useGoogle = ({ clientId, mocked = false }) => {
  const [contacts, setContacts] = useState([]);
  const [token, setToken] = useState(undefined);
  const [authorizationStatus, setAuthorizationStatus] = useState(
    AuthorizationStatus.INITIATED
  );

  const mockedConnections = () => {
    return new Promise((resolve) => {
      import('./data/google_connections_sample.json').then(async (c) => {
        await timeout(2000); // delaying things on purpose so we can see the spinner when mocking data
        resolve(c.connections);
      });
    });
  };

  /**
   *  callback method called when the athlete signs into their google account.
   *  This method attempts to retrieve contacts from the authenticated google account.
   */
  const handleSignInContactsScope = (tokenResponse) => {
    if (tokenResponse && tokenResponse.access_token) {
      const accessToken = tokenResponse.access_token;
      setToken(accessToken);

      // TODO [fumba] - change the scope to https://www.googleapis.com/auth/contacts.readonly
      // once strava app is verified by google.
      const requestedScope = 'https://www.googleapis.com/auth/userinfo.profile';
      if (
        window.google.accounts.oauth2.hasGrantedAllScopes(
          tokenResponse,
          requestedScope
        )
      ) {
        const headers = { Authorization: `Bearer ${accessToken}` };
        const url = mocked
          ? 'https://www.googleapis.com/oauth2/v3/userinfo'
          : `${PEOPLE_BASE_URL}people/me/connections?personFields=names,photos,emailAddresses`;

        setAuthorizationStatus(AuthorizationStatus.AUTHORIZED);
        createNetworkingClient({ headers })
          .get(url, { params: { alt: 'json' } })
          .then(async (response) => {
            if (response && response.status === 200) {
              let { connections } = response.data;
              if (mocked) {
                connections = await mockedConnections();
              }
              setContacts(connections);
              setAuthorizationStatus(AuthorizationStatus.DATA_FETCHED);
            } else {
              setAuthorizationStatus(AuthorizationStatus.ERROR); // TODO : log sentry error in the monorepo
            }
          })
          .catch(() => {
            setAuthorizationStatus(AuthorizationStatus.ERROR); // TODO : log sentry error in the monorepo
          });
      } else {
        // unauthorized
        setAuthorizationStatus(AuthorizationStatus.UNAUTHORIZED);
        window.google.accounts.oauth2.revoke(accessToken);
      }
    }
  };

  /**
   * Initializes a client that is used by athletes to request a token
   */
  const initializeGsiTokenClient = (scope, callback) => {
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope,
      callback
    });
    return client;
  };

  /**
   * injects GSI javascript library when the component that uses this hook is mounted
   */
  useEffect(() => {
    injectGoogleScript(async () => {
      if (mocked) {
        await timeout(2000);
      }
      setAuthorizationStatus(AuthorizationStatus.READY);
    });

    return () => {
      window.google.accounts.id.cancel();
    };
  }, [mocked]);

  const requestContacts = () => {
    setAuthorizationStatus(AuthorizationStatus.READY);
    setContacts([]);

    // TODO [fumba] - change the scope to https://www.googleapis.com/auth/contacts.readonly
    // once strava app is verified by google.
    const requestedScope = 'https://www.googleapis.com/auth/userinfo.profile';

    const client = initializeGsiTokenClient(
      requestedScope,
      handleSignInContactsScope
    );
    client.requestAccessToken();
  };

  const disconnect = () => {
    setContacts([]);
    setAuthorizationStatus(AuthorizationStatus.READY);
    if (!isNil(token)) {
      window.google.accounts.oauth2.revoke(token);
    }
  };

  return {
    authorizationStatus,
    contacts,
    requestContacts,
    disconnect
  };
};

export default useGoogle;
